import { useMemo, useState } from 'react';

import {
  useAttachFileToVersionApi,
  useBulkApplyContextStatusesApi,
  useBulkUpdateSubscriptionsApi,
  useDetachFileFromVersionApi,
  useExportUpdatesApi,
  useExportUpdatesToTracexApi,
  useGenerateImpactApi,
  useGetProfileApi,
  useGetReferenceDetailApi,
  useGetUpdateDetailApi,
  useListAllDocumentsApi,
  useListReferencesApi,
  useListSourcesApi,
  useListSourcesWithSubscriptionsApi,
  useListUpdatesApi,
  useCreateProductApi,
  useDeleteProductApi,
  useOnboardingClearSubscriptionsApi,
  useOnboardingCompleteProfileApi,
  useOnboardingGenerateProfileApi,
  useOnboardingRecommendRegulationsApi,
  useOnboardingRecommendGuidancesApi,
  useOnboardingRecommendStandardsApi,
  useOnboardingRecommendNewsApi,
  useResetOnboardingApi,
  useUpdateProductApi,
  useUpdateContextAssigneesApi,
  useUpdateContextStatusApi,
  useUpdateProfileApi,
  useUpdateSourceSubscriptionAssigneesApi,
} from '@/api/regulatoryWatch';
import { useFilesUploadService } from '@/services/sources';
import {
  BulkUpdateSubscriptionsBody,
  ImplementationStatus,
  ListAllDocumentsQuery,
  ListUpdatesQuery,
  OnboardingCompleteProfileResponse,
  UpdateProfileBody,
} from '@ditto/shared';
import { compareGeographyCodesForFilter } from '@/shared/geography';
import { getSourceIconUrl } from '@/utils/source-icon';

const ISO_FILTER_VALUE = '__iso__';
const IEC_FILTER_VALUE = '__iec__';

/** Align with list-all-documents aggregation for ISO / IEC standard sources. */
const ISO_STANDARD_TITLE_PATTERN = /^ISO(?=[\s/\-]|$)/i;
const IEC_STANDARD_TITLE_PATTERN = /^IEC(?=[\s/\-]|$)/i;

function isIsoStandardTitle(title: string): boolean {
  return ISO_STANDARD_TITLE_PATTERN.test(title);
}

function isIecStandardTitle(title: string): boolean {
  return IEC_STANDARD_TITLE_PATTERN.test(title);
}

function pickStandardOptionIconUrl(
  sourceIds: string[],
  sources: Array<{ id: string; icon: string | null }> | null | undefined
): string | undefined {
  const sortedIds = [...sourceIds].sort((a, b) => a.localeCompare(b));
  for (const id of sortedIds) {
    const s = sources?.find(x => x.id === id);
    if (s?.icon) return getSourceIconUrl(s.id, s.icon);
  }
  return undefined;
}

export const useSourcesService = () => {
  const { data: sources } = useListSourcesApi();
  const { data: sourcesWithSubscriptions } =
    useListSourcesWithSubscriptionsApi();

  const sourceLabelsMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const s of sources ?? []) {
      map[s.id] = s.title;
    }
    return map;
  }, [sources]);

  const subscribedSourceIds = useMemo(() => {
    return new Set(
      (sourcesWithSubscriptions ?? [])
        .filter(s => s.subscriptionStatus === 'subscribed')
        .map(s => s.id)
    );
  }, [sourcesWithSubscriptions]);

  const subscribedStandardSources = useMemo(() => {
    return (sourcesWithSubscriptions ?? []).filter(
      s =>
        subscribedSourceIds.has(s.id) && s.referenceTypes.includes('standard')
    );
  }, [sourcesWithSubscriptions, subscribedSourceIds]);

  const isoStandardSourceIds = useMemo(
    () =>
      subscribedStandardSources
        .filter(s => isIsoStandardTitle(s.title))
        .map(s => s.id),
    [subscribedStandardSources]
  );

  const iecStandardSourceIds = useMemo(
    () =>
      subscribedStandardSources
        .filter(s => isIecStandardTitle(s.title))
        .map(s => s.id),
    [subscribedStandardSources]
  );

  const otherStandardSourceIds = useMemo(
    () =>
      subscribedStandardSources
        .filter(
          s => !isIsoStandardTitle(s.title) && !isIecStandardTitle(s.title)
        )
        .map(s => s.id),
    [subscribedStandardSources]
  );

  // Group non-standard sources by title so duplicates (e.g. same agency
  // with regulation + guidance + news types) appear as a single filter option.
  // Only include sources the workspace is subscribed to.
  const sourceGroupMap = useMemo(() => {
    const map = new Map<
      string,
      { ids: string[]; geography: string; icon: string | null }
    >();
    for (const s of sourcesWithSubscriptions ?? []) {
      if (
        s.id === 'unknown' ||
        !subscribedSourceIds.has(s.id) ||
        !s.referenceTypes.some(
          t => t === 'regulation' || t === 'guidance' || t === 'news'
        )
      )
        continue;
      const existing = map.get(s.title);
      if (existing) {
        existing.ids.push(s.id);
      } else {
        map.set(s.title, {
          ids: [s.id],
          geography: s.geography ?? '',
          icon: s.icon ?? null,
        });
      }
    }
    return map;
  }, [sourcesWithSubscriptions, subscribedSourceIds]);

  const sourceOptionsForFilter = useMemo(() => {
    const options: {
      label: string;
      value: string;
      icon?: string;
      alignIconSlot?: boolean;
    }[] = [];

    for (const [title, { ids, icon }] of sourceGroupMap) {
      // Use the first source ID as the option value; expandSourceFilter
      // will resolve it to all IDs sharing the same title.
      const iconUrl = getSourceIconUrl(ids[0], icon);
      options.push(
        iconUrl
          ? { label: title, value: ids[0], icon: iconUrl }
          : { label: title, value: ids[0], alignIconSlot: true }
      );
    }

    if (isoStandardSourceIds.length > 0) {
      const isoIconUrl = pickStandardOptionIconUrl(
        isoStandardSourceIds,
        sourcesWithSubscriptions
      );
      options.push(
        isoIconUrl
          ? { label: 'ISO', value: ISO_FILTER_VALUE, icon: isoIconUrl }
          : { label: 'ISO', value: ISO_FILTER_VALUE, alignIconSlot: true }
      );
    }

    if (iecStandardSourceIds.length > 0) {
      const iecIconUrl = pickStandardOptionIconUrl(
        iecStandardSourceIds,
        sourcesWithSubscriptions
      );
      options.push(
        iecIconUrl
          ? { label: 'IEC', value: IEC_FILTER_VALUE, icon: iecIconUrl }
          : { label: 'IEC', value: IEC_FILTER_VALUE, alignIconSlot: true }
      );
    }

    const othersSorted = [...otherStandardSourceIds].sort((a, b) => {
      const ta =
        (sourcesWithSubscriptions ?? []).find(x => x.id === a)?.title ?? '';
      const tb =
        (sourcesWithSubscriptions ?? []).find(x => x.id === b)?.title ?? '';
      return ta.localeCompare(tb);
    });

    for (const id of othersSorted) {
      const s = (sourcesWithSubscriptions ?? []).find(x => x.id === id);
      if (!s) continue;
      const iconUrl = getSourceIconUrl(s.id, s.icon);
      options.push(
        iconUrl
          ? { label: s.title, value: id, icon: iconUrl }
          : { label: s.title, value: id, alignIconSlot: true }
      );
    }

    return options;
  }, [
    sourceGroupMap,
    isoStandardSourceIds,
    iecStandardSourceIds,
    otherStandardSourceIds,
    sourcesWithSubscriptions,
  ]);

  // Expand a selected filter value to all actual source IDs it represents.
  const expandSourceFilter = (selectedSources: string[]): string[] => {
    const expanded: string[] = [];
    for (const value of selectedSources) {
      if (value === ISO_FILTER_VALUE) {
        expanded.push(...isoStandardSourceIds);
      } else if (value === IEC_FILTER_VALUE) {
        expanded.push(...iecStandardSourceIds);
      } else {
        // Find the group this source belongs to and expand to all IDs
        let found = false;
        for (const { ids } of sourceGroupMap.values()) {
          if (ids.includes(value)) {
            expanded.push(...ids);
            found = true;
            break;
          }
        }
        if (!found) expanded.push(value);
      }
    }
    return expanded;
  };

  const geographyCodes = useMemo(() => {
    const subscribedSources = (sourcesWithSubscriptions ?? []).filter(
      s => s.subscriptionStatus === 'subscribed'
    );
    const codes = new Set<string>();
    for (const s of subscribedSources) {
      if (s.geography && s.geography !== 'unknown') codes.add(s.geography);
      for (const g of s.institutionGeographies ?? []) {
        if (g !== 'unknown') codes.add(g);
      }
    }
    return [...codes].sort(compareGeographyCodesForFilter);
  }, [sourcesWithSubscriptions]);

  return {
    sources: sources ?? [],
    sourceLabelsMap,
    sourceOptionsForFilter,
    geographyCodes,
    expandSourceFilter,
  };
};

export const useRegulatoryWatchService = (
  filters?: ListUpdatesQuery,
  options?: { refetchInterval?: number | false }
) => {
  const { data, ...rest } = useListUpdatesApi(filters, options);

  const updates = useMemo(() => {
    return data ?? [];
  }, [data]);

  return { updates, ...rest };
};

export const useUpdateContextStatusService = () => {
  const { mutate: update, isPending } = useUpdateContextStatusApi();

  const updateStatus = (
    id: string,
    status?: 'to_review' | 'applicable' | 'not_applicable' | 'out_of_scope',
    statusJustification?: string,
    options?: { onSuccess?: () => void; onError?: () => void }
  ) => {
    update(
      { id, body: { status, statusJustification } },
      { onSuccess: options?.onSuccess, onError: options?.onError }
    );
  };

  const updatePriority = (
    id: string,
    priority: 'urgent' | 'high' | 'medium' | 'low' | 'none'
  ) => {
    update({ id, body: { priority } });
  };

  const updateImpactSummary = (
    id: string,
    impactSummary: string,
    options?: { onSuccess?: () => void; onError?: () => void }
  ) => {
    update(
      { id, body: { impactSummary } },
      { onSuccess: options?.onSuccess, onError: options?.onError }
    );
  };

  const updateSuggestedActions = (
    id: string,
    suggestedActions: string,
    options?: { onSuccess?: () => void; onError?: () => void }
  ) => {
    update(
      { id, body: { suggestedActions } },
      { onSuccess: options?.onSuccess, onError: options?.onError }
    );
  };

  const updateActionPlan = (
    id: string,
    actionPlan: string,
    options?: { onSuccess?: () => void; onError?: () => void }
  ) => {
    update(
      { id, body: { actionPlan } },
      { onSuccess: options?.onSuccess, onError: options?.onError }
    );
  };

  const updateSupportingEvidence = (
    id: string,
    supportingEvidence: string,
    options?: { onSuccess?: () => void; onError?: () => void }
  ) => {
    update(
      { id, body: { supportingEvidence } },
      { onSuccess: options?.onSuccess, onError: options?.onError }
    );
  };

  const updateImplementationStatus = (
    id: string,
    implementationStatus: ImplementationStatus | null,
    options?: { onSuccess?: () => void; onError?: () => void }
  ) => {
    update(
      { id, body: { implementationStatus } },
      { onSuccess: options?.onSuccess, onError: options?.onError }
    );
  };

  return {
    updateStatus,
    updatePriority,
    updateImpactSummary,
    updateSuggestedActions,
    updateActionPlan,
    updateSupportingEvidence,
    updateImplementationStatus,
    isPending,
  };
};

export const useBulkApplyContextStatusesService = () => {
  const { mutate, isPending } = useBulkApplyContextStatusesApi();

  const bulkApplyStatuses = (
    items: { id: string; status: 'applicable' | 'not_applicable' }[],
    options?: { onSuccess?: () => void; onError?: () => void }
  ) => {
    mutate(items, {
      onSuccess: options?.onSuccess,
      onError: options?.onError,
    });
  };

  return { bulkApplyStatuses, isBulkApplying: isPending };
};

export const useProfileService = (options?: {
  refetchInterval?: number | false;
}) => {
  const { data: profile, isPending: isProfileLoading } = useGetProfileApi({
    refetchInterval: options?.refetchInterval,
  });
  const { mutate: updateProfile, isPending: isUpdating } =
    useUpdateProfileApi();

  const handleUpdateProfile = (
    data: UpdateProfileBody & { onSuccess?: () => void }
  ) => {
    const { onSuccess, ...body } = data;
    updateProfile(body, { onSuccess });
  };

  return {
    profile: profile ?? null,
    isProfileLoading,
    isOnboardingCompleted: profile?.onboardingCompleted ?? false,
    updateProfile: handleUpdateProfile,
    isUpdating,
  };
};

export const useProductsService = () => {
  const { mutate: createProduct, isPending: isCreating } =
    useCreateProductApi();
  const { mutate: updateProductMutation, isPending: isUpdatingProduct } =
    useUpdateProductApi();
  const { mutate: deleteProductMutation, isPending: isDeleting } =
    useDeleteProductApi();

  return {
    createProduct: (
      name: string,
      description?: string,
      options?: { onSuccess?: (data: { id: string }) => void }
    ) =>
      createProduct({ name, description }, { onSuccess: options?.onSuccess }),
    updateProduct: (
      productId: string,
      body: { name?: string; description?: string; position?: number }
    ) => updateProductMutation({ productId, body }),
    deleteProduct: (productId: string) => deleteProductMutation(productId),
    isCreating,
    isUpdatingProduct,
    isDeleting,
  };
};

export const useOnboardingGenerateProfileService = () => {
  const { mutate: generate, isPending: isGenerating } =
    useOnboardingGenerateProfileApi();

  const onboardingGenerateProfile = (
    url: string,
    options?: {
      onSuccess?: (data: { success: boolean }) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    generate(
      { url },
      { onSuccess: options?.onSuccess, onError: options?.onError }
    );
  };

  return { onboardingGenerateProfile, isGenerating };
};

export const useOnboardingCompleteProfileService = () => {
  const { mutate: completeProfile, isPending: isCompleting } =
    useOnboardingCompleteProfileApi();

  const onboardingCompleteProfile = (options?: {
    onSuccess?: (data: OnboardingCompleteProfileResponse) => void;
    onError?: (error: Error) => void;
  }) => {
    completeProfile(undefined, {
      onSuccess: options?.onSuccess,
      onError: options?.onError,
    });
  };

  return { onboardingCompleteProfile, isCompleting };
};

export const useOnboardingRecommendCategoriesService = () => {
  const { mutateAsync: clearSubscriptions } =
    useOnboardingClearSubscriptionsApi();
  const { mutate: recRegulations, isPending: isRegulationsLoading } =
    useOnboardingRecommendRegulationsApi();
  const { mutate: recGuidances, isPending: isGuidancesLoading } =
    useOnboardingRecommendGuidancesApi();
  const { mutate: recStandards, isPending: isStandardsLoading } =
    useOnboardingRecommendStandardsApi();
  const { mutate: recNews, isPending: isNewsLoading } =
    useOnboardingRecommendNewsApi();

  const fireAll = async (options?: { onError?: (error: Error) => void }) => {
    // Clear all existing subscriptions first, then fire 4 in parallel
    await clearSubscriptions(undefined);
    const opts = { onError: options?.onError };
    recRegulations(undefined, opts);
    recGuidances(undefined, opts);
    recStandards(undefined, opts);
    recNews(undefined, opts);
  };

  return {
    fireAll,
    isRegulationsLoading,
    isGuidancesLoading,
    isStandardsLoading,
    isNewsLoading,
    isAnyLoading:
      isRegulationsLoading ||
      isGuidancesLoading ||
      isStandardsLoading ||
      isNewsLoading,
  };
};

export const useReferencesService = (filters?: {
  search?: string;
  source?: string;
  type?: string;
}) => {
  const { data: references, isPending: isReferencesLoading } =
    useListReferencesApi(filters);

  return {
    references: references ?? [],
    isReferencesLoading,
  };
};

export const useSourceSubscriptionsService = () => {
  const { data: sourcesWithSubscriptions, isPending: isLoading } =
    useListSourcesWithSubscriptionsApi();
  const { mutate: bulkUpdate, isPending: isBulkUpdating } =
    useBulkUpdateSubscriptionsApi();

  const handleBulkUpdateSubscriptions = (
    body: BulkUpdateSubscriptionsBody,
    options?: { onSuccess?: () => void; onSettled?: () => void }
  ) => {
    bulkUpdate(body, {
      onSuccess: options?.onSuccess,
      onSettled: options?.onSettled,
    });
  };

  return {
    sourcesWithSubscriptions: sourcesWithSubscriptions ?? [],
    isLoading,
    bulkUpdateSubscriptions: handleBulkUpdateSubscriptions,
    isBulkUpdating,
  };
};

export const useUpdateDetailService = (updateId: string | undefined) => {
  const [isPolling, setIsPolling] = useState(false);

  const { data, isPending, error } = useGetUpdateDetailApi(updateId, {
    refetchInterval: isPolling ? 3000 : false,
  });

  const isGenerating = data?.context?.aiImpactStatus === 'generating';

  // Start polling when generating, stop when done
  if (isGenerating && !isPolling) {
    setIsPolling(true);
  } else if (!isGenerating && isPolling) {
    setIsPolling(false);
  }

  return {
    detail: data ?? null,
    isPending,
    error,
  };
};

export const useGenerateImpactService = () => {
  const { mutate: generate, isPending: isGenerating } = useGenerateImpactApi();

  const generateImpact = (contextId: string, updateId: string) => {
    generate({ contextId, updateId });
  };

  return { generateImpact, isGenerating };
};

export const useVersionFileUploadService = () => {
  const { uploadFiles, isUploading } = useFilesUploadService();
  const { mutate: attachFile, isPending: isAttaching } =
    useAttachFileToVersionApi();

  const uploadAndAttach = (
    file: File,
    versionId: string,
    options?: { onSuccess?: () => void }
  ) => {
    uploadFiles([file], 'shown').then(({ prismaFiles }) => {
      const registeredFile = prismaFiles[0];
      if (!registeredFile) return;

      attachFile(
        { versionId, body: { fileId: registeredFile.id } },
        { onSuccess: options?.onSuccess }
      );
    });
  };

  return {
    uploadAndAttach,
    isUploading: isUploading || isAttaching,
  };
};

export const useVersionFileDetachService = () => {
  const { mutate: detachFile, isPending: isDetaching } =
    useDetachFileFromVersionApi();

  const detach = (versionId: string, fileId: string) => {
    detachFile({ versionId, fileId });
  };

  return { detach, isDetaching };
};

export const useExportUpdatesService = () => {
  const { mutateAsync: exportUpdates, isPending: isExporting } =
    useExportUpdatesApi();

  const downloadExport = async (filters: ListUpdatesQuery) => {
    const result = await exportUpdates(filters);
    window.open(result.signedUrl, '_blank');
    return result;
  };

  return { downloadExport, isExporting };
};

export const useExportUpdatesToTracexService = () => {
  const { mutateAsync: exportToTracex, isPending: isExportingTracex } =
    useExportUpdatesToTracexApi();

  const exportTracex = async (filters: ListUpdatesQuery) => {
    return exportToTracex(filters);
  };

  return { exportTracex, isExportingTracex };
};

export const useResetOnboardingService = () => {
  const { mutate: resetOnboarding, isPending: isResetting } =
    useResetOnboardingApi();

  const handleResetOnboarding = (options?: { onSuccess?: () => void }) => {
    resetOnboarding(undefined, { onSuccess: options?.onSuccess });
  };

  return { resetOnboarding: handleResetOnboarding, isResetting };
};

export const useAllDocumentsService = (filters?: ListAllDocumentsQuery) => {
  const { data, isPending, isFetching } = useListAllDocumentsApi(filters);

  return {
    allDocuments: data ?? null,
    isLoading: isPending,
    isFetching,
  };
};

export const useReferenceDetailService = (referenceId: string | undefined) => {
  const { data, isPending, error } = useGetReferenceDetailApi(referenceId);

  return {
    referenceDetail: data ?? null,
    isPending,
    error,
  };
};

export const useUpdateContextAssigneesService = () => {
  const mutation = useUpdateContextAssigneesApi();

  return {
    updateAssignees: mutation.mutate,
    isPending: mutation.isPending,
  };
};

export const useUpdateSourceSubscriptionAssigneesService = () => {
  const mutation = useUpdateSourceSubscriptionAssigneesApi();

  return {
    updateSourceAssignees: mutation.mutate,
    isPending: mutation.isPending,
  };
};
